import { useMemo } from 'react';
import type { ExpertReportBlockTreeNode } from './types';
import { useExpertReportsBlocksResponses } from './useExpertReportsBlocksResponses';

/** Чем читается результат: справочником своих значений или баллом с размерностью */
export type ExpertReportResultShape = {
  /** Справочник, которым оценивается результат; `null` — справочника у результата нет */
  possibleValues: string[] | null;
  /** Размерность балла; `null` — размерность не определена */
  unit: string | null;
};

/** Вид результатов одного заключения: итога и каждого его блока */
export type ExpertReportResultShapes = {
  total: ExpertReportResultShape;
  blocks: Map<number, ExpertReportResultShape>;
};

/** Соответствие `reportId → вид результатов заключения` */
export type ExpertReportResultShapesMap = Map<number, ExpertReportResultShapes>;

/** Вид результата, когда о заключении ещё ничего не известно */
export const UNKNOWN_RESULT_SHAPE: ExpertReportResultShape = {
  possibleValues: null,
  unit: null,
};

const collectBlockShapes = (blocks: ExpertReportBlockTreeNode[], acc: Map<number, ExpertReportResultShape>) => {
  for (const block of blocks) {
    acc.set(block.id, { possibleValues: block.possibleValues, unit: block.unit });
    if (block.children?.length) collectBlockShapes(block.children, acc);
  }
  return acc;
};

/**
 * Вид результатов указанных экспертных заключений: справочник значений и размерность у итога
 * и у каждого оцениваемого элемента. Читается из деревьев элементов
 * ({@link useExpertReportsBlocksResponses}) — тех же, по которым строятся колонки заключений,
 * поэтому ключи вида совпадают с элементами результата в обоих режимах модели.
 *
 * Нужен там, где вид результата определяет саму подачу колонки (отбор выбором из справочника
 * против диапазона баллов), — знать его надо до того, как придут сами результаты. Карточка
 * заключения для этого не годится по праву доступа — см. `ExpertReportBlocksResponse`.
 */
export const useExpertReportsResultShapes = (reportIds: number[]): ExpertReportResultShapesMap => {
  const responses = useExpertReportsBlocksResponses(reportIds);

  return useMemo(() => {
    const map: ExpertReportResultShapesMap = new Map();
    responses.forEach((response, reportId) => {
      map.set(reportId, {
        total: { possibleValues: response.possibleValues, unit: response.unit },
        blocks: collectBlockShapes(response.blocks, new Map()),
      });
    });
    return map;
  }, [responses]);
};
